/**
 * File Upload Component for Assets
 * Provides drag-and-drop file upload with validation and progress indicators
 */

import React, { useState, useCallback, useRef } from "react";
import { useMutation } from "@apollo/client";
import {
  GENERATE_ASSET_UPLOAD_URL,
  ASSOCIATE_FILE_WITH_ASSET,
  UPLOAD_FILE,
} from "../../services/graphql/asset-operations";
import { AssetFile, UploadUrl } from "../../types/asset-types";

interface FileUploadProps {
  assetId?: string;
  onFilesUploaded?: (files: AssetFile[]) => void;
  onUploadProgress?: (progress: number) => void;
  maxFiles?: number;
  maxFileSize?: number; // in bytes
  acceptedTypes?: string[];
  disabled?: boolean;
  className?: string;
}

interface UploadingFile {
  file: File;
  progress: number;
  status: "uploading" | "completed" | "error";
  error?: string;
  fileId?: string;
}

const DEFAULT_ACCEPTED_TYPES = [
  "application/pdf",
  "image/jpeg",
  "image/png",
  "image/gif",
  "image/webp",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
];

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

export const FileUpload: React.FC<FileUploadProps> = ({
  assetId,
  onFilesUploaded,
  onUploadProgress,
  maxFiles = 10,
  maxFileSize = MAX_FILE_SIZE,
  acceptedTypes = DEFAULT_ACCEPTED_TYPES,
  disabled = false,
  className = "",
}) => {
  const [uploadingFiles, setUploadingFiles] = useState<UploadingFile[]>([]);
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [generateUploadUrl] = useMutation(GENERATE_ASSET_UPLOAD_URL);
  const [associateFile] = useMutation(ASSOCIATE_FILE_WITH_ASSET);
  const [uploadFile] = useMutation(UPLOAD_FILE);

  const validateFile = useCallback(
    (file: File): string | null => {
      if (file.size > maxFileSize) {
        return `File size must be less than ${Math.round(
          maxFileSize / 1024 / 1024
        )}MB`;
      }

      if (!acceptedTypes.includes(file.type)) {
        return `File type ${file.type} is not supported`;
      }

      return null;
    },
    [maxFileSize, acceptedTypes]
  );

  const uploadFileHandler = async (file: File): Promise<AssetFile | null> => {
    try {
      let uploadData: UploadUrl;

      if (assetId) {
        // Asset-specific upload
        const { data } = await generateUploadUrl({
          variables: {
            assetId,
            filename: file.name,
            contentType: file.type,
          },
        });
        uploadData = data.generateAssetUploadUrl;
      } else {
        // General file upload for AI content
        const { data } = await uploadFile({
          variables: {
            input: {
              fileName: file.name,
              fileSize: file.size,
              mimeType: file.type,
              description: "AI Chat content file",
              isPublic: false,
            },
          },
        });
        uploadData = data.uploadFile;
      }

      // Upload file to S3
      const uploadResponse = await fetch(uploadData.uploadUrl, {
        method: "PUT",
        body: file,
        headers: {
          "Content-Type": file.type,
        },
      });

      if (!uploadResponse.ok) {
        throw new Error("Failed to upload file to S3");
      }

      // Associate file with asset if assetId is provided
      if (assetId) {
        const { data: associateData } = await associateFile({
          variables: {
            assetId,
            fileId: uploadData.fileId,
          },
        });

        // Find the uploaded file in the response
        const uploadedFile = associateData.associateFileWithAsset.files.find(
          (f: AssetFile) => f.id === uploadData.fileId
        );

        return uploadedFile || null;
      }

      // Return basic file info if no asset association
      return {
        id: uploadData.fileId,
        filename: file.name,
        contentType: file.type,
        size: file.size,
        s3Key: uploadData.s3Key,
        uploadedAt: new Date().toISOString(),
        uploadedBy: "current-user", // This should come from auth context
      };
    } catch (error) {
      console.error("File upload error:", error);
      throw error;
    }
  };

  const handleFiles = useCallback(
    async (files: FileList | File[]) => {
      const fileArray = Array.from(files);

      // Validate files
      const validFiles: File[] = [];
      const errors: string[] = [];

      for (const file of fileArray) {
        const error = validateFile(file);
        if (error) {
          errors.push(`${file.name}: ${error}`);
        } else {
          validFiles.push(file);
        }
      }

      // Check max files limit
      if (uploadingFiles.length + validFiles.length > maxFiles) {
        errors.push(`Maximum ${maxFiles} files allowed`);
        return;
      }

      if (errors.length > 0) {
        alert(errors.join("\n"));
        return;
      }

      // Start uploading files
      const newUploadingFiles: UploadingFile[] = validFiles.map((file) => ({
        file,
        progress: 0,
        status: "uploading" as const,
      }));

      setUploadingFiles((prev) => [...prev, ...newUploadingFiles]);

      const uploadedFiles: AssetFile[] = [];

      // Upload files sequentially to avoid overwhelming the server
      for (let i = 0; i < validFiles.length; i++) {
        const file = validFiles[i];
        const uploadingFileIndex = uploadingFiles.length + i;

        try {
          // Update progress
          setUploadingFiles((prev) =>
            prev.map((uf, index) =>
              index === uploadingFileIndex ? { ...uf, progress: 50 } : uf
            )
          );

          const uploadedFile = await uploadFileHandler(file);

          if (uploadedFile) {
            uploadedFiles.push(uploadedFile);

            // Mark as completed
            setUploadingFiles((prev) =>
              prev.map((uf, index) =>
                index === uploadingFileIndex
                  ? {
                      ...uf,
                      progress: 100,
                      status: "completed" as const,
                      fileId: uploadedFile.id,
                    }
                  : uf
              )
            );
          }
        } catch (error) {
          // Mark as error
          setUploadingFiles((prev) =>
            prev.map((uf, index) =>
              index === uploadingFileIndex
                ? {
                    ...uf,
                    status: "error" as const,
                    error:
                      error instanceof Error ? error.message : "Upload failed",
                  }
                : uf
            )
          );
        }
      }

      // Notify parent component
      if (uploadedFiles.length > 0 && onFilesUploaded) {
        onFilesUploaded(uploadedFiles);
      }

      // Calculate overall progress
      const totalProgress =
        uploadingFiles.reduce((sum, uf) => sum + uf.progress, 0) /
        uploadingFiles.length;
      if (onUploadProgress) {
        onUploadProgress(totalProgress);
      }
    },
    [
      uploadingFiles,
      maxFiles,
      validateFile,
      uploadFile,
      onFilesUploaded,
      onUploadProgress,
    ]
  );

  const handleDragOver = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (!disabled) {
        setIsDragOver(true);
      }
    },
    [disabled]
  );

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragOver(false);

      if (disabled) return;

      const files = e.dataTransfer.files;
      if (files.length > 0) {
        handleFiles(files);
      }
    },
    [disabled, handleFiles]
  );

  const handleFileInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files;
      if (files && files.length > 0) {
        handleFiles(files);
      }
      // Reset input value to allow selecting the same file again
      e.target.value = "";
    },
    [handleFiles]
  );

  const handleClick = useCallback(() => {
    if (!disabled && fileInputRef.current) {
      fileInputRef.current.click();
    }
  }, [disabled]);

  const removeUploadingFile = useCallback((index: number) => {
    setUploadingFiles((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  const getFileTypeIcon = (contentType: string): string => {
    if (contentType.startsWith("image/")) return "🖼️";
    if (contentType === "application/pdf") return "📄";
    if (contentType.includes("word")) return "📝";
    if (contentType.includes("excel") || contentType.includes("sheet"))
      return "📊";
    return "📎";
  };

  return (
    <div className={`file-upload ${className}`}>
      {/* Drop Zone */}
      <div
        className={`
          border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors
          ${
            isDragOver
              ? "border-blue-500 bg-blue-50"
              : "border-gray-300 hover:border-gray-400"
          }
          ${disabled ? "opacity-50 cursor-not-allowed" : ""}
        `}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={handleClick}
      >
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept={acceptedTypes.join(",")}
          onChange={handleFileInputChange}
          className="hidden"
          disabled={disabled}
        />

        <div className="space-y-2">
          <div className="text-4xl">📁</div>
          <div className="text-lg font-medium text-gray-700">
            {isDragOver ? "Drop files here" : "Drop files or click to upload"}
          </div>
          <div className="text-sm text-gray-500">
            Maximum {maxFiles} files, up to{" "}
            {Math.round(maxFileSize / 1024 / 1024)}MB each
          </div>
          <div className="text-xs text-gray-400">
            Supported: PDF, Images, Word, Excel
          </div>
        </div>
      </div>

      {/* Uploading Files */}
      {uploadingFiles.length > 0 && (
        <div className="mt-4 space-y-2">
          <h4 className="font-medium text-gray-700">Uploading Files</h4>
          {uploadingFiles.map((uploadingFile, index) => (
            <div
              key={index}
              className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
            >
              <div className="flex items-center space-x-3 flex-1">
                <span className="text-lg">
                  {getFileTypeIcon(uploadingFile.file.type)}
                </span>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-gray-700 truncate">
                    {uploadingFile.file.name}
                  </div>
                  <div className="text-xs text-gray-500">
                    {formatFileSize(uploadingFile.file.size)}
                  </div>
                </div>
              </div>

              <div className="flex items-center space-x-3">
                {uploadingFile.status === "uploading" && (
                  <div className="flex items-center space-x-2">
                    <div className="w-16 bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                        style={{ width: `${uploadingFile.progress}%` }}
                      />
                    </div>
                    <span className="text-xs text-gray-500">
                      {uploadingFile.progress}%
                    </span>
                  </div>
                )}

                {uploadingFile.status === "completed" && (
                  <span className="text-green-600 text-sm">✅ Completed</span>
                )}

                {uploadingFile.status === "error" && (
                  <div className="text-red-600 text-sm">
                    ❌ {uploadingFile.error}
                  </div>
                )}

                <button
                  onClick={() => removeUploadingFile(index)}
                  className="text-gray-400 hover:text-gray-600 text-sm"
                  title="Remove"
                >
                  ✕
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default FileUpload;
